import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import { auth } from '../firebase';
import { getFirestore, doc, onSnapshot, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const db = getFirestore();

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      let unsubUser = null;
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        unsubUser = onSnapshot(userRef, snap => {
          setUserData(snap.exists() ? snap.data() : null);
        });
      } else {
        setUserData(null);
      }
      setLoading(false);
      return unsubUser;
    });
    return unsubscribeAuth;
  }, [db]);

  const signUp = useCallback(async (email, password, displayName = '') => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const user = cred.user;
    await updateProfile(user, { displayName });
    const userRef = doc(getFirestore(), 'users', user.uid);
    await setDoc(userRef, {
      displayName: displayName || email.split('@')[0],
      email,
      photoURL: user.photoURL || null,
      hasCompletedOnboarding: false,
      createdAt: serverTimestamp(),
    });
    user.sendEmailVerification?.();
  }, []);

  const signIn = useCallback((email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const user = result.user;
    
    // Check if user document exists, if not create it
    const userRef = doc(getFirestore(), 'users', user.uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      await setDoc(userRef, {
        displayName: user.displayName || user.email.split('@')[0],
        email: user.email,
        photoURL: user.photoURL || null,
        hasCompletedOnboarding: false,
        createdAt: serverTimestamp(),
      });
    }
    
    return result;
  }, []);

  const signInWithFacebook = useCallback(async () => {
    const provider = new FacebookAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const user = result.user;
    
    // Check if user document exists, if not create it
    const userRef = doc(getFirestore(), 'users', user.uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      await setDoc(userRef, {
        displayName: user.displayName || user.email.split('@')[0],
        email: user.email,
        photoURL: user.photoURL || null,
        hasCompletedOnboarding: false,
        createdAt: serverTimestamp(),
      });
    }
    
    return result;
  }, []);

  const resetPassword = useCallback(email => sendPasswordResetEmail(auth, email), []);

  const signOut = useCallback(() => firebaseSignOut(auth), []);

  // Check if user is a super admin based on userData
  const isSuperAdmin = userData?.role === 'superadmin' || userData?.isSuperAdmin === true;

  const value = {
    currentUser,
    loading,
    userData,
    isSuperAdmin,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithFacebook,
    resetPassword,
    signOut,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
